import { api, withAuthHeaders } from "@/lib/api";

export interface TenantSettings {
  id: string;
  name: string;
  legal_name: string;
  ide_number: string | null;
  tagline: string | null;
  about: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  canton: string | null;
  country: string;
  default_language: string;
  tva_subject: boolean;
  tva_number: string | null;
  default_tva_code: string;
  subdomain: string;
  custom_domain: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  iban: string | null;
  bic: string | null;
  bank_name: string | null;
  qr_iban: string | null;
  qr_reference_type: string | null;
  signatory_name: string | null;
  signatory_position: string | null;
  signatory_email: string | null;
  logo_storage_path: string | null;
  logo_mime_type: string | null;
  logo_width_px: number | null;
  logo_height_px: number | null;
  brand_primary_hex: string | null;
  brand_accent_hex: string | null;
  linkedin_url: string | null;
  facebook_url: string | null;
  cancellation_clause: string | null;
  attendance_threshold_pct: number;
  eduqua_certified_until: string | null;
  created_at: string;
  updated_at: string;
}

export type UpdateSettingsInput = Partial<
  Omit<
    TenantSettings,
    | "id"
    | "subdomain"
    | "custom_domain"
    | "logo_storage_path"
    | "logo_mime_type"
    | "logo_width_px"
    | "logo_height_px"
    | "created_at"
    | "updated_at"
  >
>;

export interface LogoUploadResponse {
  logo_storage_path: string;
  logo_public_url: string;
  logo_mime_type: string;
}

export const settingsApi = {
  get: () => api<TenantSettings>("/api/v1/settings"),
  update: (input: UpdateSettingsInput) =>
    api<TenantSettings>("/api/v1/settings", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  uploadLogo: async (file: File) => {
    const form = new FormData();
    form.append("logo", file);
    const raw = await withAuthHeaders();
    // FormData fixe son propre Content-Type avec boundary — on retire celui par défaut.
    const headers: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, string>)) {
      if (k.toLowerCase() !== "content-type") headers[k] = v;
    }
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/settings/logo`, {
      method: "POST",
      headers,
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error((body as { detail?: string })?.detail ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<LogoUploadResponse>;
  },
  /** Image enregistrée du centre. `null` s'il n'y a pas encore de logo. */
  uploadSignature: async (file: File) => {
    const form = new FormData();
    form.append("signature", file);
    const raw = await withAuthHeaders();
    const headers: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, string>)) {
      if (k.toLowerCase() !== "content-type") headers[k] = v;
    }
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/settings/signature`, {
      method: "POST",
      headers,
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error((body as { detail?: string })?.detail ?? `HTTP ${res.status}`);
    }
  },
  fetchLogo: async (): Promise<Blob | null> => {
    const headers = await withAuthHeaders();
    headers.Accept = "image/png,image/jpeg,image/*";
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/settings/logo`, { headers });
    if (res.status === 404) return null;
    if (!res.ok) return null;
    return res.blob();
  },
  logoPublicUrl: (path: string | null) => {
    if (!path) return null;
    const encoded = path.split("/").map(encodeURIComponent).join("/");
    return `${import.meta.env.VITE_API_URL}/public/assets/skola-assets/${encoded}`;
  },

  /**
   * Tenant reset — wipe all business data (formations, sessions, learners,
   * enrollments, rooms, QR codes, and generated files are deleted. The tenant
   * identity, user_profiles, and audit_log are PRESERVED so access and proof
   * of the reset remain available.
   *
   * `confirmation` must match the tenant subdomain verbatim.
   */
  reset: (confirmation: string) =>
    api<{
      tenant_id: string;
      reset_at: string;
      rows_deleted_by_table: Record<string, number>;
      rooms_deleted: number;
    }>("/api/v1/settings/reset", {
      method: "POST",
      body: JSON.stringify({ confirmation }),
    }),
};
